import React from 'react';
import Tooltip from '@atlaskit/tooltip';
import css from '@emotion/css';
import { token } from '@atlaskit/tokens';
import { ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';
import { useLocation } from 'react-router-dom';
import Spinner from '@atlaskit/spinner';

import {
  colors,
  SPACE_MINIMUM,
  grid,
  alignItemsCenter,
  flexStart,
  SPACE_COZY,
  borderColor,
  spaceTopComfortable,
  spaceGapComfortable,
  spaceGapCozy,
  textAlignCenter,
  marginAuto,
  flexCenter,
  flexDirectionColumn,
  spaceTopCozy,
  textTransformLowercase,
} from '@/styles';
import { ActionButtons, KeyValue, BreachCount } from '@/components/primitive';
import { typeToExperience } from '@/common/ufo/experiences';
import { CRUDableType } from '@/meta';

import { ProductEntityLink, TeamEntityLink } from '../entity-link';

/* FIXME: Cleanup:  @components/composed/capability-list/index.tsx should be combined here */

function renderCell(
  cell: TableProps['rows'][number],
  key: 'name' | 'description' | 'breaches' | 'qualityIssues',
  type: TableProps['type'],
  locationSearch: string = ''
) {
  let cellContent: React.ReactNode;

  switch (key) {
    case 'description':
      // NOTE: This means overflow text is still accessible
      cellContent = cell[key] ? (
        <Tooltip content={cell[key]}>
          <span>{cell[key]}</span>
        </Tooltip>
      ) : (
        cell[key]
      );
      break;
    case 'name': {
      if (type === 'team') {
        cellContent = (
          <h4>
            <TeamEntityLink
              name={cell.name}
              publicId={cell.publicId}
              locationSearch={locationSearch}
            />
          </h4>
        );
      } else {
        cellContent = (
          <h4>
            <ProductEntityLink
              name={cell.name}
              publicId={cell.publicId}
              locationSearch={locationSearch}
            />
          </h4>
        );
      }
      break;
    }
    case 'breaches':
      cellContent = (
        <KeyValue title="Breaching SLOs">
          <BreachCount count={cell[key]} />
        </KeyValue>
      );

      break;
    case 'qualityIssues':
      cellContent = (
        <KeyValue title="With Quality Issue SLOs">
          <BreachCount isWarning count={cell[key]} />
        </KeyValue>
      );

      break;
    default:
      // TODO: Delete when the API includes this in the response
      cellContent = <BreachCount count={cell[key]} />;
  }

  return cellContent;
}

const tableStyles = css`
  display: grid;
  margin-top: ${SPACE_COZY}px;
`;

const tableNoBorderTopStyles = css`
  & > div:nth-of-type(1) {
    border-top: none;
  }
`;

const tableBorderStyles = css`
  &:last-child {
    border-bottom: 2px solid ${borderColor};
  }

  &:nth-of-type(1) {
    border-top: 2px solid ${borderColor};
  }
`;

const tableRowOuterStyles = [
  tableBorderStyles,
  css`
    &:hover {
      background-color: ${token('color.background.neutral.subtle.hovered', colors.N10A)};
    }
  `,
];

const tableRowInnerStyles = css`
  display: flex;
  justify-content: space-between;
  padding: ${SPACE_MINIMUM}px 0px;
`;

type TableProps = {
  isLoading: boolean;
  rows: (API.Team | API.Product)[];
  name: string;
  type: 'product' | 'team';
  experienceSeq: number;
  entityType?: CRUDableType;
} & Pick<React.HTMLProps<HTMLDivElement>, 'className'>;

const Table: React.FC<TableProps> = ({
  isLoading,
  name,
  rows = [],
  type,
  className,
  experienceSeq,
  entityType,
}) => {
  const { search } = useLocation();
  const experience = typeToExperience(type);
  const getActions = ({ publicId, name: entityName }: API.Entity) => (
    <ActionButtons id={publicId} entityName={entityName} type={type} />
  );

  return (
    <div className={className}>
      {isLoading && (
        <div css={[spaceTopComfortable, textAlignCenter]}>
          <Spinner css={marginAuto} size="large" />
        </div>
      )}
      {!isLoading && (
        <>
          {!entityType && (
            <div>
              <h3>{name}</h3>
            </div>
          )}

          <div css={[tableStyles, entityType ? [tableNoBorderTopStyles] : '']}>
            {rows.length > 0 ? (
              rows.map(row => (
                <div css={tableRowOuterStyles} key={row.name}>
                  <div css={tableRowInnerStyles}>
                    <div
                      css={[
                        grid,
                        alignItemsCenter,
                        spaceGapComfortable,
                        css`
                          grid-template-columns: max-content auto;
                        `,
                      ]}
                    >
                      {renderCell(row, 'name', type, search)}
                      <div css={[flexStart, spaceGapCozy]}>
                        {renderCell(row, 'breaches', type, search)}
                        {entityType && renderCell(row, 'qualityIssues', type, search)}
                      </div>
                    </div>
                    {getActions(row)}
                  </div>
                </div>
              ))
            ) : (
              <div css={[flexCenter, flexDirectionColumn, spaceTopCozy, spaceGapCozy]}>
                <img src="/assets/not-found.svg" alt="not-found" />
                <p>
                  No <span css={textTransformLowercase}>{name}</span> found for the selected search
                </p>
              </div>
            )}
          </div>
          <UFOExperienceSuccess key={experienceSeq} experience={experience} />
        </>
      )}
    </div>
  );
};

export default React.memo(Table);
